import { BillingItem, BillingTaxTemplate, InvoiceItem } from '../../types'

export type InvoiceLineTotals = {
  subtotal: number
  discountAmount: number
  taxableAmount: number
  taxAmount: number
  total: number
}

export type InvoiceTotals = {
  subtotal: number
  totalDiscount: number
  totalTax: number
  totalAmount: number
}

export function formatCurrency(amount: number) {
  return new Intl.NumberFormat('en-ZM', {
    style: 'currency',
    currency: 'ZMW',
  }).format(amount)
}

export function formatSignedCurrency(amount: number) {
  if (amount === 0) return formatCurrency(0)
  return `${amount > 0 ? '+' : '-'}${formatCurrency(Math.abs(amount))}`
}

export function calculateInvoiceLine(item: InvoiceItem): InvoiceLineTotals {
  const subtotal = safeNumber(item.quantity) * safeNumber(item.unitPrice)
  const discountAmount = subtotal * (clampPercentage(item.discount) / 100)
  const taxableAmount = subtotal - discountAmount
  const taxDirection =
    item.taxType === 'Deduction' ? -1 : item.taxType === 'Exempt' ? 0 : 1
  const taxAmount =
    taxableAmount * (clampPercentage(item.tax) / 100) * taxDirection

  return {
    subtotal,
    discountAmount,
    taxableAmount,
    taxAmount,
    total: taxableAmount + taxAmount,
  }
}

export function recalculateInvoiceItem(item: InvoiceItem): InvoiceItem {
  const normalized = {
    ...item,
    quantity: Math.max(0, safeNumber(item.quantity)),
    unitPrice: Math.max(0, safeNumber(item.unitPrice)),
    discount: clampPercentage(item.discount),
    tax: clampPercentage(item.tax),
  }
  return {
    ...normalized,
    total: calculateInvoiceLine(normalized).total,
  }
}

export function calculateInvoiceTotals(items: InvoiceItem[]): InvoiceTotals {
  return items.reduce<InvoiceTotals>(
    (totals, item) => {
      const line = calculateInvoiceLine(item)
      return {
        subtotal: totals.subtotal + line.subtotal,
        totalDiscount: totals.totalDiscount + line.discountAmount,
        totalTax: totals.totalTax + line.taxAmount,
        totalAmount: totals.totalAmount + line.total,
      }
    },
    { subtotal: 0, totalDiscount: 0, totalTax: 0, totalAmount: 0 },
  )
}

export function buildInvoiceItem(
  billingItem: BillingItem,
  taxTemplate: BillingTaxTemplate,
  options: {
    description?: string
    quantity?: number
  } = {},
): InvoiceItem {
  return recalculateInvoiceItem({
    id: `inv-item-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    itemId: billingItem.id,
    serviceName: billingItem.serviceName,
    description: options.description ?? billingItem.description ?? '',
    quantity: options.quantity ?? 1,
    unitPrice: billingItem.unitPrice,
    discount: 0,
    discountEligible: billingItem.discountEligibility,
    tax: taxTemplate.rate,
    taxProfileId: taxTemplate.id,
    taxProfileName: taxTemplate.name,
    taxType: taxTemplate.type,
    total: 0,
  })
}

export function applyTaxTemplate(
  item: InvoiceItem,
  taxTemplate: BillingTaxTemplate,
): InvoiceItem {
  return recalculateInvoiceItem({
    ...item,
    tax: taxTemplate.rate,
    taxProfileId: taxTemplate.id,
    taxProfileName: taxTemplate.name,
    taxType: taxTemplate.type,
  })
}

export function getTaxDisplayName(item: InvoiceItem) {
  if (item.taxProfileName) return item.taxProfileName
  if (item.taxType === 'Deduction') return 'Medical Scheme deduction'
  if (item.taxType === 'Exempt' || item.tax === 0) return 'Tax Exempt'
  return 'Tax'
}

export function getTaxRateLabel(item: InvoiceItem) {
  const typeLabel =
    item.taxType === 'Deduction'
      ? 'deduction'
      : item.taxType === 'Exempt'
        ? 'exempt'
        : 'charge'
  return `${safeNumber(item.tax)}% ${typeLabel}`
}

function safeNumber(value: number) {
  return Number.isFinite(value) ? value : 0
}

function clampPercentage(value: number) {
  return Math.min(100, Math.max(0, safeNumber(value)))
}
